import React from 'react';
import { Wrapper } from './EventStatusBadge.styles';
import { EventType, MeetingStatus } from '../../logic/events/events.model';
import { useTranslation } from 'react-i18next';

export type EventStatusBadgeProps = {
  status: MeetingStatus;
  type: EventType;
  startDate?: string | null;
  finishDate?: string | null;
  withDate?: boolean;
};

export const EventStatusBadge: React.FC<EventStatusBadgeProps> = ({
  status,
  type,
  startDate,
  finishDate,
  withDate = false,
}) => {
  const { t, i18n } = useTranslation('common');

  const dateFormatter = (d: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(d));

  const isCircular = type === EventType.CIRCULAR;

  const renderLabel = () => {
    switch (status) {
      case MeetingStatus.Draft:
        if (isCircular) {
          if (withDate && startDate) {
            return t('eventStatus.circular.DraftScheduled', {
              date: dateFormatter(startDate),
            });
          }
          return t('eventStatus.circular.Draft');
        }
        return t('eventStatus.live.Draft');
      case MeetingStatus.InProgress:
        if (isCircular && withDate && finishDate) {
          return `${t('eventStatus.circular.InProgress')} ${t('eventStatus.openUntil', {
            date: dateFormatter(finishDate),
          })}`;
        }
        return isCircular
          ? t('eventStatus.circular.InProgress')
          : t('eventStatus.live.InProgress');
      case MeetingStatus.FinishRequested:
        return isCircular
          ? t('eventStatus.circular.FinishRequested')
          : t('eventStatus.live.FinishRequested');
      case MeetingStatus.Finished:
        return isCircular
          ? t('eventStatus.circular.Finished')
          : t('eventStatus.live.Finished');
      default:
        return status;
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case MeetingStatus.Draft:
        if (isCircular && startDate) {
          return 'DraftScheduled';
        }
        return isCircular ? 'DraftManual' : 'DraftLive';
      case MeetingStatus.InProgress:
        return 'InProgress';
      case MeetingStatus.FinishRequested:
        return 'FinishRequested';
      case MeetingStatus.Finished:
        return 'Finished';
      default:
        return status;
    }
  };

  return (
    <Wrapper className={`${status} ${getStatusClass()}`}>
      <div className="dot" />
      <span className="label-text">{renderLabel()}</span>
    </Wrapper>
  );
};

export default EventStatusBadge;
